import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, AlertCircle, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ClaimMetadata } from "@/types/claims";
import {
    METADATA_FIELDS,
    REQUIRED_FIELDS,
    FIELDS_BY_CATEGORY,
    getValue,
    formatValue,
    validateField,
    type MetadataField,
} from "../metadata-fields";

// --- Field Row ---

interface FieldRowProps {
    field: MetadataField;
    extractedValue: any;
    manualValue: string;
    isUsingManual: boolean;
    validationError: string | null;
    onToggleManual: (key: string) => void;
    onManualChange: (key: string, value: string) => void;
}

function FieldRow({
    field,
    extractedValue,
    manualValue,
    isUsingManual,
    validationError,
    onToggleManual,
    onManualChange,
}: FieldRowProps) {
    const hasExtracted = !!extractedValue;
    const displayValue = formatValue(extractedValue);

    return (
        <div className="pb-2">
            <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-medium text-muted-foreground">
                    {field.label}
                    {REQUIRED_FIELDS.has(field.key) && <span className="text-red-500 ml-0.5">*</span>}
                </Label>
                {validationError && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/10 border border-red-500/20">
                        <AlertCircle className="h-2.5 w-2.5 text-red-500" />
                        <span className="text-[10px] font-medium text-red-600 dark:text-red-400">{validationError}</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-2">
                {/* Extracted Value */}
                <button
                    type="button"
                    onClick={() => { if (hasExtracted && isUsingManual) onToggleManual(field.key); }}
                    disabled={!hasExtracted}
                    className={cn(
                        "relative flex items-start space-x-2 rounded-md border p-2 transition-all text-left",
                        !isUsingManual && hasExtracted
                            ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20"
                            : hasExtracted
                                ? "border-border hover:bg-muted/50 cursor-pointer"
                                : "border-border/50 opacity-50 cursor-not-allowed",
                        validationError && !isUsingManual && "border-red-500 bg-red-50/10"
                    )}
                >
                    <div className="flex-1 text-xs space-y-0.5">
                        <div className="flex items-center gap-1">
                            {!isUsingManual && hasExtracted && <Check className="h-3 w-3 text-blue-500" />}
                            <FileText className="h-3 w-3 text-blue-500" />
                            <span className="font-medium text-blue-600 dark:text-blue-400">Extracted</span>
                        </div>
                        <div className="text-foreground font-medium break-words">{displayValue}</div>
                    </div>
                </button>

                {/* Manual Entry */}
                <div
                    role={!isUsingManual ? "button" : undefined}
                    tabIndex={!isUsingManual ? 0 : undefined}
                    aria-label={!isUsingManual ? `Switch to manual entry for ${field.label}` : undefined}
                    className={cn(
                        "relative flex flex-col rounded-md border p-2 transition-all",
                        isUsingManual
                            ? "border-purple-500 bg-purple-50/50 dark:bg-purple-950/20"
                            : "border-border hover:bg-muted/50 cursor-pointer",
                        validationError && isUsingManual && "border-red-500"
                    )}
                    onClick={() => { if (!isUsingManual) onToggleManual(field.key); }}
                    onKeyDown={(e) => { if (!isUsingManual && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onToggleManual(field.key); } }}
                >
                    <div className="flex items-center gap-1 text-xs mb-1">
                        {isUsingManual && <Check className="h-3 w-3 text-purple-500" />}
                        <FileText className="h-3 w-3 text-purple-500" />
                        <Label htmlFor={`manual-${field.key}`} className="font-medium text-purple-600 dark:text-purple-400 cursor-pointer">
                            Manual
                        </Label>
                    </div>
                    <div className="w-full">
                        {field.enumOptions ? (
                            <select
                                id={`manual-${field.key}`}
                                aria-label={`Manual ${field.label}`}
                                value={manualValue}
                                onChange={(e) => onManualChange(field.key, e.target.value)}
                                className={cn(
                                    "w-full px-2 py-1 text-xs rounded border bg-background",
                                    validationError && isUsingManual ? "border-red-500" : "border-input focus:ring-purple-500"
                                )}
                            >
                                <option value="">Select...</option>
                                {field.enumOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                id={`manual-${field.key}`}
                                type="text"
                                aria-label={`Manual ${field.label}`}
                                value={manualValue}
                                onChange={(e) => onManualChange(field.key, e.target.value)}
                                placeholder="Enter value..."
                                className={cn(
                                    "w-full px-2 py-1 text-xs rounded border bg-background",
                                    validationError && isUsingManual ? "border-red-500" : "border-input focus:ring-purple-500"
                                )}
                            />
                        )}
                        {validationError && isUsingManual && (
                            <p className="text-[10px] text-red-500 mt-0.5">{validationError}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- Main Component ---

interface SingleEstimateMetadataReviewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    metadata: ClaimMetadata | null;
    onConfirm: (selectedMetadata: ClaimMetadata) => void;
    isProcessing?: boolean;
    fileName?: string;
}

export function SingleEstimateMetadataReview({
    open,
    onOpenChange,
    metadata,
    onConfirm,
    isProcessing,
    fileName,
}: SingleEstimateMetadataReviewProps) {
    // Track which fields use manual entry (true = manual, false = extracted)
    const [useManual, setUseManual] = useState<Record<string, boolean>>({});
    const [manualValues, setManualValues] = useState<Record<string, string>>({});
    const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});

    // Initialize: use extracted value when available, manual otherwise
    useEffect(() => {
        if (!open) return;
        const initial: Record<string, boolean> = {};
        METADATA_FIELDS.forEach((f) => {
            const extracted = getValue(metadata, f.key);
            initial[f.key] = !extracted; // use manual if no extracted value
        });
        setUseManual(initial);
        setManualValues({});
        setValidationErrors({});
    }, [open, metadata]);

    const handleToggleManual = (key: string) => {
        setUseManual((prev) => ({ ...prev, [key]: !prev[key] }));
        if (validationErrors[key]) {
            setValidationErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const handleManualChange = (key: string, value: string) => {
        setManualValues((prev) => ({ ...prev, [key]: value }));
        // Auto-switch to manual when user types
        if (!useManual[key]) {
            setUseManual((prev) => ({ ...prev, [key]: true }));
        }
        if (validationErrors[key]) {
            setValidationErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const handleConfirm = () => {
        const errors: Record<string, string> = {};
        const combinedMetadata: any = {};

        METADATA_FIELDS.forEach((field) => {
            const extractedValue = getValue(metadata, field.key);
            const isManual = useManual[field.key];
            const value = isManual ? (manualValues[field.key] || "").trim() : extractedValue;

            // Validate
            const error = validateField(field.key, String(value || ""));
            if (error) errors[field.key] = error;

            if (value) {
                combinedMetadata[field.key] = typeof value === "string" ? value.trim() : value;
            }
        });

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        onConfirm(combinedMetadata as ClaimMetadata);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90dvh] md:max-h-[90vh] p-0 overflow-hidden flex flex-col">
                <DialogHeader className="px-6 py-5 border-b bg-muted/30 flex-shrink-0">
                    <div>
                        <DialogTitle className="text-xl font-semibold">Review Extracted Metadata</DialogTitle>
                        <DialogDescription className="mt-1.5">
                            {fileName
                                ? `Extracted from: ${fileName} — review and edit values before creating the claim.`
                                : "Review extracted values and edit as needed before creating the claim."}
                        </DialogDescription>
                    </div>
                </DialogHeader>

                <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
                    <div className="px-6 py-4 space-y-4">
                        {Object.entries(FIELDS_BY_CATEGORY).map(([category, fields]) => (
                            <div key={category} className="space-y-2">
                                <h3 className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground px-1">
                                    {category}
                                </h3>
                                <div className="space-y-2 rounded-lg border bg-card/50 p-3">
                                    {fields.map((field, idx) => (
                                        <div key={field.key} className={cn(idx > 0 ? "border-t pt-2" : "")}>
                                            <FieldRow
                                                field={field}
                                                extractedValue={getValue(metadata, field.key)}
                                                manualValue={manualValues[field.key] || ""}
                                                isUsingManual={!!useManual[field.key]}
                                                validationError={validationErrors[field.key] || null}
                                                onToggleManual={handleToggleManual}
                                                onManualChange={handleManualChange}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollArea>

                <DialogFooter className="px-6 py-4 border-t bg-muted/30 flex-shrink-0">
                    <div className="flex items-center justify-between w-full">
                        <div className="text-sm text-muted-foreground">Review all fields before creating</div>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                disabled={isProcessing}
                                className="min-w-[140px]"
                                data-testid="button-create-claim"
                            >
                                {isProcessing ? "Creating..." : "Create Claim"}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
